import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
// React 19 native metadata
import { Copy, Check, Share2, ArrowLeft, Target, MessageSquare, Search, Shield, Zap, Swords, ArrowRight, Lightbulb } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  target: Target,
  message: MessageSquare,
  search: Search,
  shield: Shield,
  zap: Zap,
  swords: Swords,
};

const getScoreColor = (score) => {
  if (score >= 80) return 'text-emerald-400';
  if (score >= 60) return 'text-cyan-400';
  if (score >= 40) return 'text-amber-400';
  return 'text-red-400';
};

const getGradeBg = (grade) => {
  if (grade.startsWith('A')) return 'from-emerald-500/20 to-green-500/20 border-emerald-500/30';
  if (grade === 'B') return 'from-cyan-500/20 to-blue-500/20 border-cyan-500/30';
  if (grade === 'C') return 'from-amber-500/20 to-yellow-500/20 border-amber-500/30';
  return 'from-red-500/20 to-rose-500/20 border-red-500/30';
};

const getBarColor = (score) => {
  if (score >= 80) return 'bg-emerald-500';
  if (score >= 60) return 'bg-cyan-500';
  if (score >= 40) return 'bg-amber-500';
  return 'bg-red-500';
};

const ScoreReportPage = () => {
  const { scoreId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/strategy-score/${scoreId}`)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json(); })
      .then(d => setData(d))
      .catch(() => setError('Score not found'))
      .finally(() => setLoading(false));
  }, [scoreId]);

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareText = data ? `My website scored ${data.overall_score}/100 (${data.grade}) on the iSupplyAI Strategy Score! Check yours:` : '';

  if (loading) return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
      <div className="text-gray-400 animate-pulse">Loading strategy score...</div>
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center gap-4">
      <p className="text-gray-400">Score not found</p>
      <Link to="/strategy-score" className="text-purple-400 hover:text-purple-300" data-testid="score-back-link">Get your Strategy Score</Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="score-report-page">
      <>
        <title>{`Strategy Score: ${data.overall_score}/100 (${data.grade}) | iSupplyAI`}</title>
        <meta name="description" content={`${data.title || data.url} scored ${data.overall_score}/100 on the iSupplyAI Strategy Score. ${data.summary}`} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="iSupplyAI" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:title" content={`Strategy Score: ${data.overall_score}/100 (${data.grade}) | iSupplyAI`} />
        <meta property="og:description" content={data.summary} />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@isupplyai" />
        <meta name="twitter:creator" content="@isupplyai" />
        <meta name="twitter:title" content={`Strategy Score: ${data.overall_score}/100 (${data.grade}) | iSupplyAI`} />
        <meta name="twitter:description" content={data.summary} />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" />
      </>

      {/* Header */}
      <header className="border-b border-white/10 sticky top-0 bg-[#0a0a0f]/95 backdrop-blur-md z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="score-home-link">
            <img src="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" alt="iSupplyAI" className="h-8 w-8 object-contain" />
            <span className="text-sm font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">iSupplyAI</span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 text-gray-300 text-sm rounded-lg hover:bg-white/10 transition-colors"
              data-testid="score-copy-link"
            >
              {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 bg-white/5 text-gray-300 rounded-lg hover:bg-white/10 transition-colors"
              data-testid="score-share-twitter"
            >
              <Share2 className="w-4 h-4" />
            </a>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Score Hero */}
        <div className="text-center mb-12">
          <p className="text-sm font-semibold tracking-widest text-purple-400 uppercase mb-3">Strategy Score Report</p>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2 break-all" data-testid="score-url-title">
            {data.title || data.url}
          </h1>
          <p className="text-sm text-gray-500 mb-8">{data.url}</p>

          {/* Big Score Circle */}
          <div className={`inline-flex flex-col items-center justify-center w-40 h-40 rounded-full bg-gradient-to-br ${getGradeBg(data.grade)} border-2 mb-6`} data-testid="score-main-circle">
            <span className={`text-5xl font-bold ${getScoreColor(data.overall_score)}`}>{data.overall_score}</span>
            <span className="text-xs text-gray-400 mt-1">out of 100</span>
          </div>

          <div className="mb-6">
            <span className={`inline-block text-3xl font-black ${getScoreColor(data.overall_score)}`} data-testid="score-grade">
              Grade: {data.grade}
            </span>
          </div>

          <p className="text-gray-300 max-w-xl mx-auto text-base" data-testid="score-summary">{data.summary}</p>
        </div>

        {/* Strength & Weakness */}
        <div className="grid md:grid-cols-2 gap-4 mb-12">
          <div className="bg-emerald-500/[0.08] border border-emerald-500/20 rounded-xl p-5" data-testid="score-strength">
            <p className="text-xs font-semibold text-emerald-400 uppercase tracking-wider mb-2">#1 Strength</p>
            <p className="text-sm text-gray-200">{data.top_strength}</p>
          </div>
          <div className="bg-red-500/[0.08] border border-red-500/20 rounded-xl p-5" data-testid="score-weakness">
            <p className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-2">#1 Weakness</p>
            <p className="text-sm text-gray-200">{data.top_weakness}</p>
          </div>
        </div>

        {/* Dimension Breakdown */}
        <div className="space-y-4 mb-12" data-testid="score-dimensions">
          <h2 className="text-lg font-bold text-white mb-4">Score Breakdown</h2>
          {(data.dimensions || []).map((dim) => {
            const IconComponent = ICON_MAP[dim.icon] || Target;
            return (
              <div key={dim.key} className="bg-white/[0.03] border border-white/10 rounded-xl p-5" data-testid={`score-dim-${dim.key}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                      <IconComponent className="w-4 h-4 text-gray-400" />
                    </div>
                    <span className="font-semibold text-white">{dim.label}</span>
                  </div>
                  <span className={`text-xl font-bold ${getScoreColor(dim.score)}`}>{dim.score}</span>
                </div>
                {/* Score bar */}
                <div className="w-full h-2 bg-white/5 rounded-full mb-3 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${getBarColor(dim.score)} transition-all duration-700`}
                    style={{ width: `${dim.score}%` }}
                  />
                </div>
                <p className="text-sm text-gray-400">{dim.insight}</p>
              </div>
            );
          })}
        </div>

        {/* Improvement Steps */}
        {data.improvement_steps && data.improvement_steps.length > 0 && (
          <div className="mb-12" data-testid="score-improvement-steps">
            <div className="flex items-center gap-2 mb-4">
              <Lightbulb className="w-5 h-5 text-amber-400" />
              <h2 className="text-lg font-bold text-white">How to Improve Your Score</h2>
            </div>
            <div className="space-y-3">
              {data.improvement_steps.map((step, i) => (
                <div key={i} className="flex items-start gap-4 bg-amber-500/[0.06] border border-amber-500/20 rounded-xl p-4">
                  <div className="w-7 h-7 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-bold text-amber-400">{i + 1}</span>
                  </div>
                  <p className="text-sm text-gray-200 leading-relaxed">{step}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="text-center py-12 border-t border-white/10">
          <p className="text-sm text-gray-400 mb-3">Want to improve your score?</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/strategy-score"
              className="px-6 py-3 bg-white/5 text-gray-300 font-semibold rounded-xl hover:bg-white/10 transition-colors text-sm"
              data-testid="score-try-another"
            >
              Score Another Website
            </Link>
            <Link
              to="/"
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-purple-500/25 transition-all text-sm"
              data-testid="score-try-isupplyai"
            >
              Try iSupplyAI Free
            </Link>
          </div>
        </div>
      </div>

      <footer className="border-t border-white/10 py-6 px-4">
        <div className="max-w-4xl mx-auto text-center text-xs text-gray-500">
          <p>Powered by <Link to="/" className="text-purple-400 hover:text-purple-300">iSupplyAI</Link> — AI Strategy Platform</p>
        </div>
      </footer>
    </div>
  );
};

export default ScoreReportPage;
